import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listDevices, type ApiVendoDevice } from '@/api/vendo';
import { type ClientProfile } from '@/api/clientPortal';
import Dashboard from './Dashboard';
import SettingsPage from './SettingsPage';
import { SessionsTab, useVendoData } from '@/pages/Admin/Vendo';
import WifiVouchersCounter from '@/pages/Admin/WifiVouchersCounter';
import PromptWrapper from '@/components/Admin/Templates/PromptWrapper';
import { useUIStore } from '@/store/useUIStore';

/**
 * Basic portal shell — mirrors the operator dashboard's shape (sidebar
 * on desktop, bottom tabs on the phone) but with only three menus:
 * Dashboard, Sessions, Settings. One device, simple words.
 */

type Menu = 'dashboard' | 'sessions' | 'vouchers' | 'settings';

const MENUS: { key: Menu; label: string; icon: string }[] = [
   { key: 'dashboard', label: 'Dashboard', icon: '📊' },
   { key: 'sessions', label: 'Sessions', icon: '🕒' },
   // Advanced vouchers for the buyer's one vendo (Ray 2026-10-18).
   { key: 'vouchers', label: 'Vouchers', icon: '🎟️' },
   { key: 'settings', label: 'Settings', icon: '⚙️' },
];

const ClientPortalHome: React.FC<{
   profile: ClientProfile;
   onLogout: () => void;
}> = ({ profile, onLogout }) => {
   const [menu, setMenu] = useState<Menu>('dashboard');
   const queryClient = useQueryClient();
   // Feeds the OPERATOR-identical Sessions tab (device+session stores);
   // the server scopes both lists to this login's one device.
   useVendoData();
   // Modal host for the operator components' prompts (Add time,
   // whitelist, kick, …) — same wiring as DashboardLayout.
   const { show, template, title, data: uiData, actions } = useUIStore();
   const { data: devicesData, isLoading } = useQuery({
      queryKey: ['client-device'],
      queryFn: () => listDevices(),
      refetchInterval: 60_000,
   });
   const device: ApiVendoDevice | undefined = devicesData?.devices?.[0];
   const refresh = () =>
      queryClient.invalidateQueries({ queryKey: ['client-device'] });

   return (
      <Layout>
         <PromptWrapper
            show={show}
            template={template ?? ''}
            title={title ?? ''}
            data={uiData}
            actions={actions}
         />
         <Sidebar>
            <Brand>
               <BrandName>SmartFi</BrandName>
               <BrandSub>My Vendo Portal</BrandSub>
            </Brand>
            <NavList>
               {MENUS.map((m) => (
                  <NavBtn
                     key={m.key}
                     type="button"
                     $active={menu === m.key}
                     onClick={() => setMenu(m.key)}
                  >
                     <span>{m.icon}</span>
                     <NavLabel>{m.label}</NavLabel>
                  </NavBtn>
               ))}
            </NavList>
            <SidebarFoot>
               <FootUser>{profile.username}</FootUser>
               <LogoutBtn type="button" onClick={onLogout}>
                  Sign out
               </LogoutBtn>
            </SidebarFoot>
         </Sidebar>

         <Main>
            <TopBar>
               <TopTitle>
                  {device?.name || profile.deviceName}
                  {device && (
                     <OnlineDot
                        $on={device.status === 'online'}
                        title={device.status}
                     />
                  )}
               </TopTitle>
               <TopLogout type="button" onClick={onLogout}>
                  Sign out
               </TopLogout>
            </TopBar>

            {isLoading && <SkeletonLines lines={3} />}
            {!isLoading && !device && (
               <Loading>
                  Your vendo is not available. Ask the seller to check your
                  login.
               </Loading>
            )}
            {device && menu === 'dashboard' && (
               <Dashboard device={device} profile={profile} onChanged={refresh} />
            )}
            {device && menu === 'sessions' && <SessionsTab />}
            {device && menu === 'vouchers' && <WifiVouchersCounter fixedDeviceId={String(device._id)} />}
            {device && menu === 'settings' && (
               <SettingsPage
                  device={device}
                  profile={profile}
                  onChanged={refresh}
               />
            )}
            <Footer>Powered by SmartFi · seller: {profile.sellerEmail}</Footer>
         </Main>

         <BottomNav>
            {MENUS.map((m) => (
               <BottomBtn
                  key={m.key}
                  type="button"
                  $active={menu === m.key}
                  onClick={() => setMenu(m.key)}
               >
                  <span>{m.icon}</span>
                  {m.label}
               </BottomBtn>
            ))}
         </BottomNav>
      </Layout>
   );
};

export default ClientPortalHome;

const Layout = styled.div`
   min-height: 100dvh;
   background: var(--app-bg, #f4f6fa);
   display: flex;
`;

const Sidebar = styled.aside`
   width: 210px;
   flex-shrink: 0;
   background: var(--app-surface, #fff);
   border-right: 1px solid var(--app-border, #e3e8ef);
   padding: 18px 12px;
   display: none;
   flex-direction: column;
   @media (min-width: 800px) {
      display: flex;
   }
`;

const Brand = styled.div`
   padding: 0 8px 14px;
`;

const BrandName = styled.div`
   font-size: 16px;
   font-weight: 800;
`;

const BrandSub = styled.div`
   font-size: 11px;
   color: var(--app-text-muted, #7b8794);
`;

const NavList = styled.nav`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const NavBtn = styled.button<{ $active: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   border: none;
   background: ${({ $active }) => ($active ? '#4f7cff14' : 'transparent')};
   color: ${({ $active }) => ($active ? '#4f7cff' : 'var(--app-text-dim, #55606c)')};
   border-radius: 9px;
   padding: 10px 12px;
   font-size: 13.5px;
   font-weight: 700;
   cursor: pointer;
   text-align: left;
`;

const NavLabel = styled.span``;

const SidebarFoot = styled.div`
   margin-top: auto;
   padding: 12px 8px 0;
   border-top: 1px solid var(--app-border, #e3e8ef);
`;

const FootUser = styled.div`
   font-size: 11.5px;
   font-weight: 700;
   font-family: ui-monospace, monospace;
   overflow-wrap: anywhere;
`;

const LogoutBtn = styled.button`
   margin-top: 8px;
   width: 100%;
   border: 1px solid var(--app-border, #e3e8ef);
   background: transparent;
   border-radius: 8px;
   padding: 7px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   color: var(--app-text-dim, #55606c);
`;

const Main = styled.main`
   flex: 1;
   min-width: 0;
   /* Same content width as the operator dashboard (Ray: 760px was too tight). */
   max-width: 1200px;
   margin: 0 auto;
   padding: 14px 14px 84px;
   @media (min-width: 800px) {
      padding-bottom: 30px;
   }
`;

const TopBar = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   padding: 4px 2px 12px;
`;

const TopTitle = styled.h1`
   font-size: 19px;
   font-weight: 800;
   display: flex;
   align-items: center;
   gap: 8px;
`;

const OnlineDot = styled.span<{ $on: boolean }>`
   width: 10px;
   height: 10px;
   border-radius: 50%;
   background: ${({ $on }) => ($on ? '#198754' : '#dc3545')};
`;

const TopLogout = styled.button`
   border: 1px solid var(--app-border, #e3e8ef);
   background: var(--app-surface, #fff);
   border-radius: 8px;
   padding: 7px 12px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   @media (min-width: 800px) {
      display: none;
   }
`;

const Loading = styled.div`
   padding: 60px 20px;
   text-align: center;
   font-size: 14px;
   color: var(--app-text-dim, #55606c);
`;

const Footer = styled.div`
   margin-top: 22px;
   text-align: center;
   font-size: 11px;
   color: var(--app-text-muted, #7b8794);
`;

const BottomNav = styled.nav`
   position: fixed;
   left: 0;
   right: 0;
   bottom: 0;
   display: flex;
   background: var(--app-surface, #fff);
   border-top: 1px solid var(--app-border, #e3e8ef);
   padding: 6px 8px calc(6px + env(safe-area-inset-bottom));
   @media (min-width: 800px) {
      display: none;
   }
`;

const BottomBtn = styled.button<{ $active: boolean }>`
   flex: 1;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 2px;
   border: none;
   background: transparent;
   color: ${({ $active }) => ($active ? '#4f7cff' : 'var(--app-text-muted, #7b8794)')};
   font-size: 10.5px;
   font-weight: 700;
   padding: 6px 0;
   cursor: pointer;
   span {
      font-size: 17px;
   }
`;
